import { useEffect, useRef, useCallback } from 'react';
import { UI } from '@/config/constants';

/**
 * Custom hook to trap focus within a modal or dialog component.
 * Implements WCAG 2.1 Success Criterion 2.1.2 (No Keyboard Trap)
 *
 * @param isActive - Whether the focus trap is active
 * @param options - Configuration options
 * @returns A ref to attach to the modal container element
 *
 * @example
 * ```tsx
 * const Modal = ({ isOpen, onClose }) => {
 *   const modalRef = useFocusTrap(isOpen, {
 *     onEscape: onClose,
 *     restoreFocus: true
 *   });
 *   return (
 *     <div ref={modalRef}>
 *       <button onClick={onClose}>Close</button>
 *       <input type="text" />
 *     </div>
 *   );
 * };
 * ```
 */
export function useFocusTrap<T extends HTMLElement = HTMLDivElement>(
  isActive = true,
  options?: {
    onEscape?: () => void;
    restoreFocus?: boolean;
    initialFocus?: boolean;
  }
) {
  const elementRef = useRef<T>(null);
  const previousActiveElement = useRef<HTMLElement | null>(null);
  const wasActive = useRef(false);

  const {
    onEscape,
    restoreFocus = true,
    initialFocus = true,
  } = options || {};

  // Stable ref for onEscape so the effect doesn't re-run when the callback changes
  const onEscapeRef = useRef(onEscape);
  onEscapeRef.current = onEscape;

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    // Handle Escape key
    if (e.key === 'Escape' && onEscapeRef.current) {
      onEscapeRef.current();
      return;
    }

    // Handle Tab key to trap focus
    if (e.key === 'Tab') {
      const element = elementRef.current;
      if (!element) return;

      const focusableSelectors = [
        'a[href]',
        'button:not([disabled])',
        'textarea:not([disabled])',
        'input:not([disabled]):not([type="hidden"])',
        'select:not([disabled])',
        '[tabindex]:not([tabindex="-1"])',
      ].join(', ');

      const focusableElements = Array.from(element.querySelectorAll<HTMLElement>(focusableSelectors));
      if (focusableElements.length === 0) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      // Shift + Tab on first element -> focus last element
      if (e.shiftKey && document.activeElement === firstElement) {
        e.preventDefault();
        lastElement.focus();
      }
      // Tab on last element -> focus first element
      else if (!e.shiftKey && document.activeElement === lastElement) {
        e.preventDefault();
        firstElement.focus();
      }
    }
  }, []);

  useEffect(() => {
    if (!isActive) {
      wasActive.current = false;
      return;
    }

    const element = elementRef.current;
    if (!element) return;

    // Only set initial focus on activation (false → true), not on every re-render
    const justActivated = !wasActive.current;
    wasActive.current = true;

    // Save current focus to restore later
    if (justActivated && restoreFocus) {
      previousActiveElement.current = document.activeElement as HTMLElement;
    }

    // Set initial focus only when first activated
    if (justActivated && initialFocus) {
      const focusableSelectors = [
        'a[href]',
        'button:not([disabled])',
        'textarea:not([disabled])',
        'input:not([disabled]):not([type="hidden"])',
        'select:not([disabled])',
        '[tabindex]:not([tabindex="-1"])',
      ].join(', ');

      const focusableElements = Array.from(element.querySelectorAll<HTMLElement>(focusableSelectors));
      if (focusableElements.length > 0) {
        setTimeout(() => {
          focusableElements[0].focus();
        }, UI.FOCUS_TRAP_INITIAL_DELAY_MS);
      }
    }

    // Add event listener
    document.addEventListener('keydown', handleKeyDown);

    // Cleanup: remove listener and restore focus
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      if (restoreFocus && previousActiveElement.current) {
        previousActiveElement.current.focus();
      }
    };
  }, [isActive, restoreFocus, initialFocus, handleKeyDown]);

  return elementRef;
}
